'use client';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useDateFormatter } from '@terra/ui-formatters';
import { formatCloudProviderLabel } from '@terra/shared-contracts-integrations';
import type {
  CloudInventorySurfaceAccountBodyDto,
  CloudInventorySurfaceAttachmentDto,
  CloudInventorySurfaceProviderBodyDto,
  UpdateCloudAccountPolicyBodyDto,
} from '@terra/shared-contracts-external-network';
import { MetaDefault } from '../../../components/typography';
import { ConfirmationDialog } from '../../confirmation-dialog/ConfirmationDialog';
import { SettingsCard } from '../../settings/SettingsCard';
import { toasterApi } from '../../toast/ToastProvider';
import { CloudAccountCatalogRow } from './CloudAccountCatalogRow';
import { InventoryInfoAlert } from './InventoryInfoAlert';
import { InventoryIntegrationsLink } from './InventoryIntegrationsLink';
import { InventoryLoadFailedAlert } from './InventoryLoadFailedAlert';
import { InventoryNotConnectedCard } from './InventoryNotConnectedCard';
import {
  attachmentKey,
  createAutoScopeCollection,
  toAccountBody,
  toSyncBody,
} from './cloud-inventory-attach.utils';
import { notifyInventoryFailure } from './notify-inventory-failure';

export type CloudInventoryPendingState = {
  isAttaching: boolean;
  isDetaching: boolean;
  isSyncing: boolean;
  isUpdatingPolicy: boolean;
};

export type CloudInventoryAttachSectionProps = {
  attachments: CloudInventorySurfaceAttachmentDto[];
  isLoading: boolean;
  isError: boolean;
  isReadOnly: boolean;
  pending: CloudInventoryPendingState;
  onAttach: (body: CloudInventorySurfaceAccountBodyDto) => Promise<unknown>;
  onDetach: (body: CloudInventorySurfaceAccountBodyDto) => Promise<unknown>;
  onSync: (body: CloudInventorySurfaceProviderBodyDto) => Promise<unknown>;
  onUpdatePolicy: (input: {
    accountId: string;
    body: UpdateCloudAccountPolicyBodyDto;
  }) => Promise<unknown>;
  onOpenIntegrations: () => void;
};

export function CloudInventoryAttachSection({
  attachments,
  isLoading,
  isError,
  isReadOnly,
  pending,
  onAttach,
  onDetach,
  onSync,
  onUpdatePolicy,
  onOpenIntegrations,
}: CloudInventoryAttachSectionProps) {
  const t = useTranslations(
    'designSystem.networkSettings.externalInventory.cloudAccounts',
  );
  const tShared = useTranslations(
    'designSystem.networkSettings.externalInventory.shared',
  );
  const dateFormatter = useDateFormatter();
  const [pendingInScopeAccountId, setPendingInScopeAccountId] = useState<
    string | null
  >(null);
  const isBusy =
    pending.isAttaching ||
    pending.isDetaching ||
    pending.isSyncing ||
    pending.isUpdatingPolicy;
  const scopeCollection = useMemo(
    () =>
      createAutoScopeCollection({
        labels: {
          IN_SCOPE: t('scope.IN_SCOPE'),
          PENDING: t('scope.PENDING'),
          OUT_OF_SCOPE: t('scope.OUT_OF_SCOPE'),
        },
      }),
    [t],
  );

  const handleAttach = async (
    attachment: CloudInventorySurfaceAttachmentDto,
  ) => {
    const body = toAccountBody(attachment);

    if (!body) {
      return;
    }

    try {
      await onAttach(body);
      toasterApi.success({ title: t('attachSuccess') });
    } catch (error) {
      notifyInventoryFailure({ title: t('attachFailed'), error });
    }
  };

  const handleDetach = async (
    attachment: CloudInventorySurfaceAttachmentDto,
  ) => {
    const body = toAccountBody(attachment);

    if (!body) {
      return;
    }

    try {
      await onDetach(body);
      toasterApi.success({ title: t('detachSuccess') });
    } catch (error) {
      notifyInventoryFailure({ title: t('detachFailed'), error });
    }
  };

  const handleSync = async (attachment: CloudInventorySurfaceAttachmentDto) => {
    try {
      await onSync(toSyncBody(attachment));
      toasterApi.success({ title: tShared('runSyncSuccess') });
    } catch (error) {
      notifyInventoryFailure({
        title: tShared('runSyncFailed'),
        error,
        fallbackDescription: tShared('runSyncFailedDescription'),
      });
    }
  };

  const handlePolicyChange = async ({
    accountId,
    body,
  }: {
    accountId: string;
    body: UpdateCloudAccountPolicyBodyDto;
  }) => {
    try {
      await onUpdatePolicy({ accountId, body });
      toasterApi.success({ title: t('updateSuccess') });
    } catch (error) {
      notifyInventoryFailure({
        title: t('updateFailed'),
        error,
        fallbackDescription: t('updateFailedDescription'),
      });
    }
  };

  const hasInScopePolicy = attachments.some(
    (attachment) => attachment.attached && attachment.autoScope === 'IN_SCOPE',
  );

  if (isError) {
    return <InventoryLoadFailedAlert message={t('loadFailed')} />;
  }

  if (!isLoading && attachments.length === 0) {
    return (
      <InventoryNotConnectedCard
        title={t('notConnectedTitle')}
        description={t('notConnectedDescription')}
        onOpenIntegrations={onOpenIntegrations}
      />
    );
  }

  return (
    <>
      {hasInScopePolicy ? (
        <InventoryInfoAlert message={t('inScopeWarning')} />
      ) : null}
      <SettingsCard
        isLoading={isLoading}
        footer={
          <MetaDefault color="fg.neutral.subtle">
            {t.rich('iamHelp', {
              link: (chunks) => (
                <InventoryIntegrationsLink onClick={onOpenIntegrations}>
                  {chunks}
                </InventoryIntegrationsLink>
              ),
            })}
          </MetaDefault>
        }
      >
        {attachments.map((attachment) => (
          <CloudAccountCatalogRow
            key={attachmentKey(attachment)}
            attachment={attachment}
            isReadOnly={isReadOnly}
            isBusy={isBusy}
            isSyncing={pending.isSyncing}
            isAttaching={pending.isAttaching}
            isDetaching={pending.isDetaching}
            lastSyncedText={
              attachment.lastSyncedAt
                ? t('lastSynced', {
                    date: dateFormatter.format(
                      new Date(attachment.lastSyncedAt),
                    ),
                  })
                : t('neverSynced')
            }
            neverSyncedLabel={t('neverSynced')}
            scopeCollection={scopeCollection}
            onAttach={(next) => void handleAttach(next)}
            onDetach={(next) => void handleDetach(next)}
            onSync={(next) => void handleSync(next)}
            onEnabledChange={({ accountId, autoScope, enabled }) =>
              void handlePolicyChange({
                accountId,
                body: { autoScope, enabled },
              })
            }
            onAutoScopeChange={({ accountId, autoScope }) =>
              void handlePolicyChange({ accountId, body: { autoScope } })
            }
            onRequestInScope={setPendingInScopeAccountId}
            labels={{
              enabled: t('enabledLabel'),
              scopePlaceholder: t('scopePlaceholder'),
              attach: t('attach', {
                provider: formatCloudProviderLabel({
                  provider: attachment.provider,
                }),
              }),
              detach: t('detach'),
              runSync: tShared('runSync'),
              status: (status) => t(`status.${status}`),
            }}
          />
        ))}
      </SettingsCard>
      <ConfirmationDialog
        isOpen={pendingInScopeAccountId !== null}
        onOpenChange={({ open }) => {
          if (!open) {
            setPendingInScopeAccountId(null);
          }
        }}
        variant="warning"
        title={t('inScopeConfirmTitle')}
        description={t('inScopeConfirmDescription')}
        confirmText={t('inScopeConfirm')}
        onConfirm={() => {
          if (!pendingInScopeAccountId) {
            return;
          }

          const accountId = pendingInScopeAccountId;

          setPendingInScopeAccountId(null);
          void handlePolicyChange({
            accountId,
            body: { autoScope: 'IN_SCOPE' },
          });
        }}
      />
    </>
  );
}
